import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Confirm, Modal } from '@components/common';
import GanttTaskTable from '@components/common/GanttTaskTable';
import MilestoneForm from '@components/ModalComponents/MilestoneForm';
import TaskForm from '@components/ModalComponents/TaskForm';

import { useMutatePortalMilestones } from '@hooks/portal/milestones/useMutatePortalMilestones';
import { useQueryPortalMilestone } from '@hooks/portal/milestones/useQueryPortalMilestone';
import { useQueryPortalAvailableProjects } from '@hooks/portal/projects/portalProjects/useQueryPortalAvailableProjects';
import { useMutatePortalTasks } from '@hooks/portal/tasks/useMutatePortalTasks';
import { useQueryPortalTask } from '@hooks/portal/tasks/useQueryPortalTask';
import { useQueryPortalTasksPriorities } from '@hooks/portal/tasks/useQueryPortalTasksPriorities';
import { useQueryPortalTasksStatuses } from '@hooks/portal/tasks/useQueryPortalTasksStatuses';
import { useQueryPortalTimeline } from '@hooks/portal/timeline/useQueryPortalTimeline';
import { useQueryPortalTimelineAssigneesFilter } from '@hooks/portal/timeline/useQueryPortalTimelineAssigneesFilter';
import { useQueryPortalTimelineMilestonesFilter } from '@hooks/portal/timeline/useQueryPortalTimelineMilestonesFilter';
import { useQueryPortalTimelinePriorities } from '@hooks/portal/timeline/useQueryPortalTimelinePriorities';
import { useQueryPortalTimelineStatuses } from '@hooks/portal/timeline/useQueryPortalTimelineStatuses';

import { portalTimeline } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	resetPortalTimeline,
	setAssignees,
	setMilestoneId,
	setPriority,
	setSearchValue,
	setStatus,
} from '@slices/portalTimeline';

import { isEmpty } from '@utils/common';
import { setFilterUtil, setMultiSelectFilterUtil } from '@utils/filterUtil';
import { prepareGanttChartData } from '@components/routes/AdminPanel/ProjectProfile/Timeline/utils';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { ITableSearch, TTableFilter } from '@interfaces/components/table';
import { Task } from '@interfaces/ganttTasks/ganttTasks';

import { EAddTypeId } from '@components/common/GanttTaskTable/enums';

import { MODAL_WIDTH_480, MODAL_WIDTH_772, modalCloseConfirm } from '@constants/modals';
import { TableFilterTypes } from '@constants/table';
import { searchTooltipText } from './constants';

import styles from './Timeline.module.scss';

interface ITimelineProps {
	projectId: string;
	isProjectActive: boolean;
}

const Timeline = ({
	projectId,
	isProjectActive,
}: ITimelineProps) => {
	const dispatch = useAppDispatch();

	const {
		searchValue,
		status,
		priority,
		assignees,
		milestoneId,
	} = useAppSelector(portalTimeline);

	const [selectedTaskStatus, setSelectedTaskStatus] = useState<TSelectOption<string>>(null);
	const [selectedTaskPriority, setSelectedTaskPriority] = useState<TSelectOption<string>>(null);
	const [selectedTaskAssignees, setSelectedTaskAssignees] = useState<TSelectOption<string>[] | null>(null);
	const [selectedMilestoneId, setSelectedMilestoneId] = useState<TSelectOption<string>>(null);

	const [isTaskEditMode, setIsTaskEditMode] = useState(false);
	const [isTaskViewMode, setIsTaskViewMode] = useState(false);
	const [isMilestoneEditMode, setIsMilestoneEditMode] = useState(false);
	const [isMilestoneViewMode, setIsMilestoneViewMode] = useState(false);

	const [isTaskFormModal, setIsTaskFormModal] = useState(false);
	const [isDirtyTaskFormModal, setIsDirtyTaskFormModal] = useState(false);
	const [isCloseConfirmTaskModal, setIsCloseConfirmTaskModal] = useState(false);
	const [editableTaskId, setEditableTaskId] = useState<string | null>(null);

	const [isMilestoneFormModal, setIsMilestoneFormModal] = useState(false);
	const [isDirtyMilestoneFormModal, setIsDirtyMilestoneFormModal] = useState(false);
	const [isCloseConfirmMilestoneModal, setIsCloseConfirmMilestoneModal] = useState(false);
	const [editableMilestoneId, setEditableMilestoneId] = useState<string | null>(null);

	const openTaskFormModal = () => setIsTaskFormModal(true);
	const openMilestoneFormModal = () => setIsMilestoneFormModal(true);

	const closeTaskModals = () => {
		setIsTaskFormModal(false);
		setIsCloseConfirmTaskModal(false);
		setIsTaskEditMode(false);
		setEditableTaskId(null);
	};

	const closeMilestoneModals = () => {
		setIsMilestoneFormModal(false);
		setIsCloseConfirmMilestoneModal(false);
		setIsMilestoneEditMode(false);
		setEditableMilestoneId(null);
	};

	const closeTaskViewModal = () => {
		setIsTaskViewMode(false);
		setEditableTaskId(null);
	};

	const closeMilestoneViewModal = () => {
		setIsMilestoneViewMode(false);
		setEditableMilestoneId(null);
	};

	const toggleCloseTaskConfirmModal = () => {
		if (isDirtyTaskFormModal) setIsCloseConfirmTaskModal(prevState => !prevState);
		else closeTaskModals();
	};

	const toggleCloseMilestoneConfirmModal = () => {
		if (isDirtyMilestoneFormModal) setIsCloseConfirmMilestoneModal(prevState => !prevState);
		else closeMilestoneModals();
	};

	const handleAdd = useCallback((type: EAddTypeId) => {
		if (type === EAddTypeId.task) {
			openTaskFormModal();
		}

		if (type === EAddTypeId.milestone) {
			openMilestoneFormModal();
		}
	}, []);

	const {
		portalTimelineMilestonesWithTasks,
		portalTimelineTasksWithoutMilestones,
		isLoadingPortalTimeline,
	} = useQueryPortalTimeline(projectId);

	const { portalTimelineStatuses, isLoadingPortalTimelineStatuses } = useQueryPortalTimelineStatuses(projectId);
	const { portalTimelinePriorities, isLoadingPortalTimelinePriorities } = useQueryPortalTimelinePriorities();
	const { portalAvailableProjects, isLoadingPortalAvailableProjects } = useQueryPortalAvailableProjects();
	const { portalTasksStatuses, isLoadingPortalTasksStatuses } = useQueryPortalTasksStatuses(projectId);
	const { portalTasksPriorities, isLoadingPortalTasksPriorities } = useQueryPortalTasksPriorities();

	const {
		portalTimelineAssigneesFilter,
		isLoadingPortalTimelineAssigneesFilter,
	} = useQueryPortalTimelineAssigneesFilter(projectId);

	const {
		portalTimelineMilestonesFilter,
		isLoadingPortalTimelineMilestonesFilter,
	} = useQueryPortalTimelineMilestonesFilter(projectId);

	const { portalTask, isFetchingPortalTask } = useQueryPortalTask(editableTaskId);
	const { portalMilestone, isFetchingPortalMilestone } = useQueryPortalMilestone(editableMilestoneId);

	const {
		createTask,
		updateTask,
	} = useMutatePortalTasks({
		projectId,
	});

	const {
		createMilestone,
		updateMilestone,
	} = useMutatePortalMilestones({
		projectId,
	});

	const tasks = useMemo(() => {
		if (isEmpty(portalTimelineMilestonesWithTasks) && isEmpty(portalTimelineTasksWithoutMilestones)) {
			return [];
		}

		return prepareGanttChartData(portalTimelineMilestonesWithTasks, portalTimelineTasksWithoutMilestones);
	}, [portalTimelineMilestonesWithTasks, portalTimelineTasksWithoutMilestones]);

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			tooltipText: searchTooltipText,
			placeholder: searchTooltipText,
		},
	], [searchValue, onSearchHandler]);

	const filters: Array<TTableFilter> = useMemo(() => {
		return [
			{
				name: 'status',
				placeholder: 'Status',
				option: selectedTaskStatus,
				options: portalTimelineStatuses,
				minWidth: '200px',
				setState: setSelectedTaskStatus,
				defaultValue: status,
			},
			{
				name: 'priority',
				placeholder: 'Priority',
				option: selectedTaskPriority,
				options: portalTimelinePriorities,
				minWidth: '200px',
				setState: setSelectedTaskPriority,
				defaultValue: priority,
			},
			{
				type: TableFilterTypes.MULTISELECT,
				name: 'assignees',
				placeholder: 'Assignee',
				option: selectedTaskAssignees,
				options: portalTimelineAssigneesFilter,
				minWidth: '200px',
				setState: setSelectedTaskAssignees,
				defaultValue: assignees,
			},
			{
				name: 'milestoneId',
				placeholder: 'Milestone',
				option: selectedMilestoneId,
				options: portalTimelineMilestonesFilter,
				minWidth: '200px',
				setState: setSelectedMilestoneId,
				defaultValue: milestoneId,
			},
		];
	}, [
		portalTimelineStatuses,
		selectedTaskStatus,
		status,
		portalTimelinePriorities,
		selectedTaskPriority,
		priority,
		assignees,
		portalTimelineAssigneesFilter,
		selectedTaskAssignees,
		milestoneId,
		selectedMilestoneId,
		portalTimelineMilestonesFilter,
	]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedTaskStatus,
			currentValue: status,
			setAction: setStatus,
			fieldName: 'status',
			dispatch,
		});
	}, [dispatch, status, selectedTaskStatus]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedTaskPriority,
			currentValue: priority,
			setAction: setPriority,
			fieldName: 'priority',
			dispatch,
		});
	}, [dispatch, priority, selectedTaskPriority]);

	useEffect(() => {
		setMultiSelectFilterUtil({
			selectedValues: selectedTaskAssignees,
			setAction: setAssignees,
			fieldName: 'assignees',
			dispatch,
		});
	}, [dispatch, selectedTaskAssignees]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedMilestoneId,
			currentValue: milestoneId,
			setAction: setMilestoneId,
			fieldName: 'milestoneId',
			dispatch,
		});
	}, [dispatch, milestoneId, selectedMilestoneId]);

	const isLoading = isLoadingPortalTimeline
		|| isLoadingPortalTimelineStatuses
		|| isLoadingPortalTimelinePriorities
		|| isLoadingPortalTimelineAssigneesFilter
		|| isLoadingPortalTimelineMilestonesFilter
		|| isLoadingPortalAvailableProjects
		|| isFetchingPortalTask
		|| isFetchingPortalMilestone
		|| isLoadingPortalTasksStatuses
		|| isLoadingPortalTasksPriorities;

	const defaultProject = (portalAvailableProjects
		? portalAvailableProjects.find(project => project?.value === projectId)
		: undefined) as TSelectOption<string>;

	const onTaskClick = useCallback((task: Task) => {
		setEditableTaskId(task.id);

		if (isProjectActive) {
			setIsTaskEditMode(true);
		} else {
			setIsTaskViewMode(true);
		}
	}, [isProjectActive]);

	const onMilestoneClick = useCallback((task: Task) => {
		setEditableMilestoneId(task.id);

		if (isProjectActive) {
			setIsMilestoneEditMode(true);
		} else {
			setIsMilestoneViewMode(true);
		}
	}, [isProjectActive]);

	useEffect(() => {
		return () => {
			dispatch(resetPortalTimeline());
		};
	}, [dispatch]);

	return (
		<div className={styles.timelineContainer}>
			<GanttTaskTable
				data={tasks}
				filters={filters}
				handleAdd={handleAdd}
				isCreatable={isProjectActive}
				isLoading={isLoading}
				onMilestoneClick={onMilestoneClick}
				onTaskClick={onTaskClick}
				search={search}
				searchThreshold={0}
			/>
			<Modal
				isDirtyForm={isDirtyTaskFormModal}
				isOpen={isTaskFormModal}
				onClose={toggleCloseTaskConfirmModal}
				title='Add task'
				width={MODAL_WIDTH_772}
			>
				<TaskForm
					closeModals={closeTaskModals}
					createTask={createTask}
					defaultProject={defaultProject}
					portalTasksPriorities={portalTasksPriorities}
					portalTasksStatuses={portalTasksStatuses}
					projectId={projectId}
					setIsDirtyFormModal={setIsDirtyTaskFormModal}
					toggleCloseConfirmModal={toggleCloseTaskConfirmModal}
				/>
			</Modal>

			<Modal
				isDirtyForm={isDirtyTaskFormModal}
				isOpen={isTaskEditMode && !isLoading}
				onClose={toggleCloseTaskConfirmModal}
				title='Edit Task'
				width={MODAL_WIDTH_772}
			>
				<TaskForm
					closeModals={closeTaskModals}
					isEditMode={isTaskEditMode}
					portalTask={portalTask}
					portalTasksPriorities={portalTasksPriorities}
					portalTasksStatuses={portalTasksStatuses}
					projectId={projectId}
					setIsDirtyFormModal={setIsDirtyTaskFormModal}
					toggleCloseConfirmModal={toggleCloseTaskConfirmModal}
					updateTask={updateTask}
				/>
			</Modal>

			<Modal
				isOpen={isTaskViewMode && !isLoading}
				onClose={closeTaskViewModal}
				title='View task'
				width={MODAL_WIDTH_772}
			>
				<TaskForm
					closeModals={closeTaskViewModal}
					isViewMode={isTaskViewMode}
					portalTask={portalTask}
					portalTasksPriorities={portalTasksPriorities}
					portalTasksStatuses={portalTasksStatuses}
					projectId={projectId}
					setIsDirtyFormModal={setIsDirtyTaskFormModal}
					toggleCloseConfirmModal={toggleCloseTaskConfirmModal}
				/>
			</Modal>

			<Modal
				isDirtyForm={isDirtyMilestoneFormModal}
				isOpen={isMilestoneFormModal}
				onClose={toggleCloseMilestoneConfirmModal}
				title='Add milestone'
				width={MODAL_WIDTH_772}
			>
				<MilestoneForm
					closeModals={closeMilestoneModals}
					createMilestone={createMilestone}
					defaultProject={defaultProject}
					projectId={projectId}
					setIsDirtyFormModal={setIsDirtyMilestoneFormModal}
					toggleCloseConfirmModal={toggleCloseMilestoneConfirmModal}
				/>
			</Modal>

			<Modal
				isDirtyForm={isDirtyMilestoneFormModal}
				isOpen={isMilestoneEditMode && !isLoading}
				onClose={toggleCloseMilestoneConfirmModal}
				title='Edit milestone'
				width={MODAL_WIDTH_772}
			>
				<MilestoneForm
					closeModals={closeMilestoneModals}
					isEditMode={isMilestoneEditMode}
					portalMilestone={portalMilestone}
					projectId={projectId}
					setIsDirtyFormModal={setIsDirtyMilestoneFormModal}
					toggleCloseConfirmModal={toggleCloseMilestoneConfirmModal}
					updateMilestone={updateMilestone}
				/>
			</Modal>

			<Modal
				isOpen={isMilestoneViewMode && !isLoading}
				onClose={closeMilestoneViewModal}
				title='View milestone'
				width={MODAL_WIDTH_772}
			>
				<MilestoneForm
					closeModals={closeMilestoneViewModal}
					isViewMode={isMilestoneViewMode}
					portalMilestone={portalMilestone}
					projectId={projectId}
					setIsDirtyFormModal={setIsDirtyMilestoneFormModal}
					toggleCloseConfirmModal={toggleCloseMilestoneConfirmModal}
				/>
			</Modal>

			<Modal
				isOpen={isCloseConfirmTaskModal}
				onClose={toggleCloseTaskConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={closeTaskModals}
					question={modalCloseConfirm.question}
				>
					{modalCloseConfirm.children}
				</Confirm>
			</Modal>

			<Modal
				isOpen={isCloseConfirmMilestoneModal}
				onClose={toggleCloseMilestoneConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={toggleCloseMilestoneConfirmModal}
					onSubmit={closeMilestoneModals}
					question={modalCloseConfirm.question}
				>
					{modalCloseConfirm.children}
				</Confirm>
			</Modal>
		</div>
	);
};

export default Timeline;
